import { Arrays, Numeric } from "../../util";
import { CalculationRowType, Employment, PayrollDetails, UserDefinedRow } from "../../model";
import { SalaryBasesRow } from "./SalaryBasesRow";
import { Ajax } from "../../api/infra/Ajax";
import { Calculations } from "../../api/crud/Calculations";
import { Payrolls } from "../../api/crud/Payrolls";
import { RowTypeMatch } from "./RowTypeMatch";
import { Culture } from "./Culture";
import { TableFormat } from "./TableFormat";
import { Tables } from "./Tables";
import { ValidationHelper } from "../ValidationHelper";

/** Experimental helper class for importing data from HRSuunti sytem to Palkkaus.fi */
export class HrSuuntiImportLogic {

  /**
   * Adds a collection of salary bases rows to a given payroll.
   * @param payrollId Identifier for the payroll where the calculations should be updated.
   * @param salaryBases The rows that should be added.
   * @param employments List of employments that are available in the current account.
   * @param ajax Instance of Ajax that is use in connecting to the server.
   * @returns A payroll object that is refreshed from the server.
   */
  public static async addToPayroll(payrollId: string, salaryBases: SalaryBasesRow[], employments: Employment[], ajax: Ajax): Promise<PayrollDetails> {
    const payrolls = new Payrolls(ajax);
    const calculations = new Calculations(ajax);
    let payroll = await payrolls.getSingle(payrollId);
    const groupByEmpId = salaryBases.reduce((group: { [key: string]: SalaryBasesRow[] }, row: SalaryBasesRow) => {
      const empId = row.employmentId;
      if (empId) {
        group[empId] = group[empId] ?? [];
        group[empId].push(row);
      }
      return group;
    }, {});
    const empIds = Object.keys(groupByEmpId).filter((x) => !!x);
    console.debug("empIds", empIds);
    const missingIds = empIds.filter((id) => !payroll.calcs.find((x) => x.worker.employmentId == id));
    if (missingIds.length) {
      console.log("Adding employments: " + missingIds);
      payroll = await payrolls.addEmployments(payroll, missingIds);
    }
    let counter = 0;
    empIds.forEach(async (key) => {
      counter++;
      const rows = groupByEmpId[key];
      const calc = payroll.calcs.find((x) => x.worker.employmentId == key);
      calc.rows.length = 0;
      const emp = employments.find((emp) => emp.id == key);
      if (emp) {
        const fixedRows = emp.work.salaryDefaults.filter((row) =>
          // TODO: Vaihda toisin päin: Kuukausi-/tuntipalkka otetaan suoraan ja sen arvot haetaan datasta.
          //       Myös AY-jäsenmaksu jne. fiksatut rivit tähän logiikkaan?
          row.rowType != CalculationRowType.MonthlySalary
          && row.rowType != CalculationRowType.HourlySalary
        );
        if (fixedRows.length) {
          fixedRows.forEach((row) => {
            row.sourceId = "employment";
          });
          calc.rows.push(...fixedRows);
        }
      }
      rows.forEach((x) => {
        const newRow: UserDefinedRow = {
          count: x.count,
          data: x.data || {},
          message: x.message,
          price: x.price,
          rowType: x.rowType,
          sourceId: "import",
        };
        if (x.costCenter) {
          newRow.accounting = newRow.accounting ?? {};
          newRow.accounting.dimensions = newRow.accounting.dimensions ?? [];
          newRow.accounting.dimensions.push( {
             id: "costCenter",
             value: x.costCenter,
           });
        }
        calc.rows.push(newRow);
      });
      console.log(`Saving calculation for ${key}, (${counter}/${empIds.length})`);
      await calculations.save(calc);
    });
    console.log(`Saving done!`);
    payroll = await payrolls.getSingle(payrollId);
    return payroll;
  }

  /**
   * Does the full import for the given arrays
   * @param input Original input from the HRSuunti software
   * @param salaryBases The salary bases that are the target of this import before creating the Payroll objects
   * @param employments The employment relations that have been created for the import.
   */
  public static doImport(input: HrSuuntiInput[], salaryBases: SalaryBasesRow[], employments: Employment[]): void {
    const output = input.map((x) => this.mapToSalaryBases(x));
    output.forEach((x) => this.resolveEmployment(x, employments));
    this.resolveTypesAndPrices(output, this.getRowTypeMapperEditable());
    salaryBases.length = 0;
    salaryBases.push(...output);
  }

  /**
   * Logic to resolve the employment relation.
   * @param row A salary bases row where we attempt to fing an employment relation.
   * @param employments All employment relations that are available for this account.
   */
  public static resolveEmployment(row: SalaryBasesRow, employments: Employment[]): void {
    const id = (row.srcEmploymentId || "").toString().trim().toLowerCase();
    if (!id) {
      return;
    }
    row.employment = employments.find((x) =>
      x.id.toLowerCase() == id ||
      x.personalInfo.officialId?.toLowerCase() == id ||
      x.personalInfo.avatar.id.toLowerCase() == id ||
      x.info.sourceId?.toLowerCase() == id
    );
    if (row.employment) {
      row.employmentId = row.employment.id;
    }
  }
  /**
   * Resolves the row types and prices based on
   * raw data that has already been mapped to SalaryBasesRow and employments have been resolved.
   * @param rows Incoming data where employments have already been resolved.
   * @param mapper The mapper that is used in mapping source row types to Salaxy row types.
   */
  public static resolveTypesAndPrices(rows: SalaryBasesRow[], mapper: RowTypeMatch[]): void {
    rows.forEach((row) => {
      // Assure that the fields are strings, not numbers
      // HACK: This should be done by the mapper based on the schema.
      row.srcEmploymentId = row.srcEmploymentId?.toString();
      row.srcRowTypeId = row.srcRowTypeId?.toString();
      row.costCenter = row.costCenter?.toString();
    });

    const rowTypes = this.getRowTypeMapper(mapper);
    rows.forEach((row) => this.resolveTypesAndPricesForRow(row, rowTypes));
    const rowsByEmplyee = Arrays.groupBy(rows, (x) => x.employmentId);
    Object.keys(rowsByEmplyee).forEach((key) => {
      rowsByEmplyee[key].filter((val) => val.data?.kind == "monthlySalary").forEach((row, ix) => {
        row.data.kind = null;
        if (ix > 0) {
          row.rowType = null;
          row.message = "Useita kk-palkkoja, vain 1. laskelmaan";
        }
      });
    });
  }

  /** Temp method for getting the editable row type mapper. */
  public static getRowTypeMapperEditable(): RowTypeMatch[] {
    const res = Tables.import(this.rowTypeRaw, TableFormat.Txt, Culture.Fi);
    res.data.forEach((row: RowTypeMatch) => {
      row.data = row.data || {};
      if (row.rowType) {
        row.rowType = row.rowType.substring(0, 1).toLowerCase() + row.rowType.substring(1) as any;
      }
      if ((row as any).rowKind) {
        row.data.kind = (row as any).rowKind.substring(0, 1).toLowerCase() + (row as any).rowKind.substring(1) as any;
        delete((row as any).rowKind);
      }
    });
    return res.data as any;
  }

  /** Gets and prepares a row type mapper for mapping */
  public static getRowTypeMapper(mapper: RowTypeMatch[]): RowTypeMatch[] {
    const mapperErrors = new ValidationHelper({});
    mapper.forEach((match) => {
      if (match.searchType == "regExp" && typeof match.search == "string") {
        try {
          match.search = new RegExp(match.search);
        } catch (ex) {
          mapperErrors.error("Invalid regex: " + ex.message);
          match.search = null;
        }
      }
      if (match.search) {
        if (match.type == "row") {
          mapperErrors.checkNotEmpty(match, "rowType", `Match of type "row" must have a rowType (${match.search})`);
        }
      }
    });
    return mapper;
  }

  /**
   * Modifies existing SalaryBasesRow that has preliminary data from the source
   * @param row Row to modify as part of mapping process
   */
  public static resolveTypesAndPricesForRow(row: SalaryBasesRow, rowTypeMap: RowTypeMatch[]): void {

    if (!row.employment) {
      return; // This should no longer be allowed in import
    }
    // TODO: Set based on employment.work.wageBasis
    const monthlySalary = row.employment.work.salaryDefaults?.find((x) => x.rowType == CalculationRowType.MonthlySalary)?.price;
    let hourlySalary = row.employment.work.salaryDefaults?.find((x) => x.rowType == CalculationRowType.HourlySalary)?.price;
    if (!hourlySalary && monthlySalary) {
      // TODO: Default should be configurable per company / usecase
      const hours = row.employment.work.monthlyHours || 160;
      hourlySalary = monthlySalary / hours;
    }
    const salaryType = monthlySalary ? CalculationRowType.MonthlySalary : (
      hourlySalary ? CalculationRowType.HourlySalary : CalculationRowType.Salary
    );

    // TODO: add validation errors
    row.srcRowTypeId = (row.srcRowTypeId || "").toString().trim();
    if (!row.srcRowTypeId) {
      row.rowType = CalculationRowType.Unknown;
      row.message = "Error: source row type is null/empty";
      return;
    }
    const match = rowTypeMap.filter((x) => !!x.search).find((x) =>
      x.searchType == "regExp" ? (x.search as RegExp).test(row.srcRowTypeId)
        : (x.search || "").toString().trim().toLowerCase() == (row.srcRowTypeId || "").trim().toLowerCase()
    );
    if (!match) {
      row.rowType = CalculationRowType.Unknown;
      row.message = "No match for " + row.srcRowTypeId;
      return;
    }
    switch (match.type) {
      case "ignore":
        row.rowType = null;
        row.message = match.message || match.comment;
        break;
      case "hourlyRow":
        row.rowType = match.rowType || salaryType;
        row.data = row.data || {};
        row.data.kind = match.data.kind;
        row.message = match.message || match.comment;
        row.price = (match.price || 1) * hourlySalary;
        row.count = (match.count == null ? 1 : match.count) * (row.count == null ? 1 : row.count);
        break;
      case "monthlySalary":
        row.rowType = CalculationRowType.MonthlySalary;
        row.data = row.data || {};
        row.data.kind = "monthlySalary";
        row.message = match.message || match.comment;
        row.price = match.price || monthlySalary;
        row.count = match.count || row.count || 1; // Add more logic if necessary
        break;
      case "row": {
        row.rowType = match.rowType;
        row.data = row.data || {};
        row.data.kind = match.data.kind;
        row.message = match.message || match.comment;
        const priceCandidates = row.employment.work.salaryDefaults.filter((x) => x.rowType == row.rowType);
        row.price = (priceCandidates.find((x) => x.rowType == row.rowType && x.data.kind == row.data.kind)?.price)
          || (priceCandidates.find((x) => x.rowType == row.rowType && !x.data.kind)?.price)
          || match.price;
      }
    }
    if (row.price) {
      // Round the JavaScript-based calculations
      row.price = Numeric.round(row.price, 6);
    }
    return;
  }

  /**
   * Does the basic mapping from HrSuuntiInput to SalaryBases object.
   * @param inputRow Input row from HR Suunti
   * @returns The input mapped as SalaryBasesRow
   */
  public static mapToSalaryBases(inputRow: HrSuuntiInput): SalaryBasesRow {
    // HACK: Replace this with a mapper
    const result: SalaryBasesRow = {
      date: inputRow.PVM,
      srcEmploymentId: inputRow.TYNRO?.toString(),
      srcRowTypeId: inputRow.PLNRO?.toString(),
      message: null,
      count: inputRow["KPL/MISTÄ"],
      price: null,
      rowType: CalculationRowType.Unknown,
      costCenter: inputRow.OSASTO?.toString(),
    };
    return result;
  }

  private static rowTypeRaw = `search	searchType	type	rowType	rowKind	count	price	message	comment
^[0-9]$	regExp	ignore						PLNRO 1-9 ei tuoda.
140		hourlyRow	HolidaySalary				Muu lomapalkka	Muu lomapalkka (perhevapaa ym)
177		row	WorkingTimeCompensation	EmergencyWorkCompensation			Hälytysraha	Tuntien tasaus TX => ei tuoda.
11		ignore						
31		row	Salary			50	Uinninvalvonta palkkio (krt)	
59		hourlyRow	PaidSickLeaveHourlySalary					Sairasajan palkka/tt
60		hourlyRow	HourlySalary					Tuntityö (ATP)
64		ignore						Tehtäväkoht. lisä => ei tuoda.
^85|94$	regExp	hourlyRow	SundayWork					Sunnuntaityö 100 %/tt OR /kk
^87|96$	regExp	hourlyRow	SaturdayAddition			0,2		Lauantaityö 20 %/tt OR Lauantaityö 20% /kk
90		hourlyRow	Overtime			0,5	Ylityöt 50%	Ylityö 50%/kk
91		hourlyRow	Overtime			1	Ylityöt 100%	Ylityö 100%/kk
99		hourlyRow	WorkingTimeCompensation	ExtraWorkPremium			Lisätyökorvaus	Lisätyökorvaus/kk
102		hourlyRow	SaturdayAddition			0,2	Aattolisä	Aattolisä/kk
108		hourlyRow				-1	Väh. kk-palkka/tunti	Väh. kk-palkka/tunti
109		hourlyRow	PaidSickLeaveMonthlySalary					Sairasajan palkka/kk/tunti
188		hourlyRow	NightimeAddition			0,45		Yötyökorvaus %/kk
^189|191$	regExp	hourlyRow	EveningAddition			0,15		Iltatyökorvaus %/kk OR %/tt
513		hourlyRow					Koulutusajan palkka/kk/tunti	Koulutusajan palkka/kk/tunti
997		monthlySalary	MonthlySalary		1			Kk-palkka
`;
}

/** Data model for the import from HR Suunti */
export interface HrSuuntiInput {
  /** Logical date for the material */
  PVM: string,
  /** Worker ID in HR Suunti system. */
  TYNRO: number,
  /** Salary row type in Suunti system */
  PLNRO: number,
  /** Some unknown data that is not supported by this import. */
  TVR: string,
  /** Amount or from */
  "KPL/MISTÄ": number,
  /** Time or until */
  "AIKA/MIHIN": number,
  /** Price in euro */
  "MK/EUR": number,
  /** Cost center (dimension 1) */
  "OSASTO": number,
}
